/**
 * Content for the question that sends users holding both the IPL and AtoZ roles
 * to the right form. AtoZ handles a product liability claim unless it meets one
 * of these exclusions, in which case it belongs to the Corporate Product
 * Liability policy and is filed as an IPL claim.
 */

export interface AtozEscalationExclusion {
  text: string;
  details?: string[];
}

export const ATOZ_ESCALATION_INTRO =
  "AtoZ handles all Product Liability claims related to products sold on Amazon " +
  "marketplace up to $1M unless they meet the below exclusions in which the claim " +
  "needs to be escalated out of the AtoZ program to the Corporate Product Liability " +
  "policy for handling.";

export const ATOZ_ESCALATION_QUESTION =
  "Does your claim meet any of the exclusions below to be considered a Corporate " +
  "Product Liability claim?";

export const ATOZ_ESCALATION_EXCLUSIONS: AtozEscalationExclusion[] = [
  {
    text: "Involve Amazon manufactured devices (Echo, Ring, Fire Stick, etc.)",
    details: [
      "Amazon can handle private brands (i.e. Amazon Basics) as long as the product is not a device",
      "These claims require special instructions on tendering to the manufacturer and handled direct by Amazon.",
    ],
  },
  { text: "Carrier Reportable Claims" },
  { text: "Bodily Injury Claim that is attorney represented" },
  { text: "Notice of Litigation" },
  {
    text: "Requesting Pain & Suffering",
    details: ["AtoZ handles BI only for medical bills/lost work"],
  },
  {
    text:
      "Interruption of critical utilities (power, water, gas) or potential serious " +
      "injury or illness that:",
    details: [
      "Is life-threatening",
      "Results in permanent impairment of a body function or permanent damage to a body structure, or",
      "Necessitates medical or surgical intervention to preclude permanent impairment of a body function or permanent damage to a body structure",
    ],
  },
  {
    text: "Bodily Injury (BI) Claims involving any of the trending products under MCF 143 (Program Type) in the CSIs:",
    details: [
      "Eye drops, Sodium Nitrite, Table top fire pits, Heated insoles, Buffalo social media, " +
        "Pressure Cooker, Button Battery, Climbing Rope/Rope, E-Bikes/E-Scooters, AC Units, " +
        "Assistive Devices",
    ],
  },
];

export type AtozEscalationAnswer = "atoz" | "corporate-pl";

export const ATOZ_ESCALATION_OPTIONS: { value: AtozEscalationAnswer; label: string }[] = [
  { value: "atoz", label: "No, continue to the AtoZ claim submission form." },
  { value: "corporate-pl", label: "Yes, continue to the Product Liability claim submission form." },
];

/**
 * Router state carried between the AtoZ and IPL pre-screens. Both ask the
 * question, so the answer travels with the navigation and the second pre-screen
 * opens with it selected instead of asking again.
 */
export interface AtozEscalationLocationState {
  atozEscalationAnswer: AtozEscalationAnswer;
}

export function escalationAnswerFromLocationState(state: unknown): AtozEscalationAnswer | null {
  if (typeof state !== "object" || state === null) {
    return null;
  }
  const answer = (state as Partial<AtozEscalationLocationState>).atozEscalationAnswer;
  return answer === "atoz" || answer === "corporate-pl" ? answer : null;
}
